/* ═══════════════════════════════════════════════════════════════
   VacationService — Vacation categories, firm holidays,
   and vacation request CRUD via PostgreSQL (local, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { VacationCategory, FirmHoliday, VacationRequest } from '../../shared/types';
import * as cache from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { TimeService } from './timeService';

function toCamel(row: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(row)) {
    result[k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())] = v;
  }
  return result;
}

export const VacationService = {
  // ─── Vacation Categories (LookupTableTab-compatible) ───
  categories: {
    list: async (): Promise<VacationCategory[]> =>
      cache.list<VacationCategory>('vacation_categories'),
    save: async (item: VacationCategory): Promise<VacationCategory> =>
      cache.save('vacation_categories', item),
    delete: async (id: string): Promise<void> => cache.remove('vacation_categories', id),
  },

  // ─── Firm Holidays ───
  holidays: {
    list: async (): Promise<FirmHoliday[]> => cache.list<FirmHoliday>('firm_holidays'),
    save: async (item: FirmHoliday): Promise<FirmHoliday> => cache.save('firm_holidays', item),
    delete: async (id: string): Promise<void> => cache.remove('firm_holidays', id),
  },

  // ─── Vacation Requests ───
  listRequests: async (memberId?: string, status?: string): Promise<VacationRequest[]> => {
    const filters: Record<string, any> = {};
    if (memberId) filters.teamMemberId = memberId;
    if (status) filters.status = status;
    if (Object.keys(filters).length > 0) {
      return cache.query<VacationRequest>('vacation_requests', filters);
    }
    return cache.list<VacationRequest>('vacation_requests');
  },

  listRequestsInRange: async (
    startDate: string,
    endDate: string,
    memberId?: string
  ): Promise<VacationRequest[]> => {
    const firmId = getCurrentFirmId();
    const filters: { col: string; op: string; val: any }[] = [
      { col: 'status', op: '=', val: 'approved' },
      { col: 'start_date', op: '<=', val: endDate },
      { col: 'end_date', op: '>=', val: startDate },
    ];
    if (memberId) filters.push({ col: 'team_member_id', op: '=', val: memberId });

    const data = await cloudApi.dbQuery('vacation_requests', firmId, filters);
    return (data || []).map((row) => toCamel(row) as VacationRequest);
  },

  saveRequest: async (req: VacationRequest): Promise<VacationRequest> =>
    cache.save('vacation_requests', req),

  /** Approving a vacation request also creates locked source_kind='vacation'
   *  forecast rows for every workday in the range — this is what wires the
   *  forecast grid + timesheet auto-fill to vacation. Idempotent: re-approving
   *  resyncs the forecasts (handy if the request's hours_per_day was edited). */
  approveRequest: async (id: string, approvedBy: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();
    await cloudApi.dbUpdate(
      'vacation_requests',
      { status: 'approved', approved_by: approvedBy, approved_at: now, updated_at: now },
      [
        { col: 'id', op: '=', val: id },
        { col: 'firm_id', op: '=', val: firmId },
      ]
    );
    cache.invalidate('vacation_requests');

    // Pull the request fresh to seed the forecast sync.
    const rows = await cloudApi.dbQuery('vacation_requests', firmId, [
      { col: 'id', op: '=', val: id },
    ]);
    const req = rows && rows[0] ? (toCamel(rows[0]) as VacationRequest) : null;
    if (req) {
      try {
        await TimeService.syncVacationToForecasts(req.id, 'create', {
          teamMemberId: req.teamMemberId,
          startDate: req.startDate,
          endDate: req.endDate,
          hoursPerDay: req.hoursPerDay || 8,
        });
      } catch (err) {
        // Forecast sync failure shouldn't block approval — log and move on.
        // The forecast grid will still show the vacation via VacationService.

        console.error('[VacationService] forecast sync failed after approve:', err);
      }
    }
  },

  denyRequest: async (id: string, deniedBy: string, reason?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();
    await cloudApi.dbUpdate(
      'vacation_requests',
      {
        status: 'denied',
        denied_by: deniedBy,
        denied_at: now,
        denial_reason: reason || null,
        updated_at: now,
      },
      [
        { col: 'id', op: '=', val: id },
        { col: 'firm_id', op: '=', val: firmId },
      ]
    );
    cache.invalidate('vacation_requests');
    // Clean up any forecast rows that were created when this request was
    // previously approved.  Safe no-op if no rows exist.
    try {
      await TimeService.syncVacationToForecasts(id, 'remove');
    } catch {}
  },

  /** Cancelling a previously-approved request must also remove the auto-
   *  generated forecast cells, otherwise the grid shows ghost vacation. */
  cancelRequest: async (id: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();
    await cloudApi.dbUpdate('vacation_requests', { status: 'cancelled', updated_at: now }, [
      { col: 'id', op: '=', val: id },
      { col: 'firm_id', op: '=', val: firmId },
    ]);
    cache.invalidate('vacation_requests');
    try {
      await TimeService.syncVacationToForecasts(id, 'remove');
    } catch {}
  },

  deleteRequest: async (id: string): Promise<void> => {
    // Same cleanup as cancel — the forecast sync is idempotent so calling
    // remove first guarantees no stranded vacation cells.
    try {
      await TimeService.syncVacationToForecasts(id, 'remove');
    } catch {}
    return cache.remove('vacation_requests', id);
  },
};
